import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useSalarySlip, useEmployees, useUpdateWorkShiftNotes } from '@/hooks/useWorkShifts';
import { useAuth } from '@/contexts/AuthContext';
import { IWorkShift } from '@/types/workshift';
import { logger } from '@/utils/logger';

// Generate year options from 2024 to current year + 1
const generateYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const years: number[] = [];
  for (let year = 2024; year <= currentYear + 1; year++) {
    years.push(year);
  }
  return years;
};

interface SalarySlipProps {
  selectedYear: number;
  selectedMonth: number;
  onDateChange: (year: number, month: number) => void;
}

const SalarySlip: React.FC<SalarySlipProps> = ({
  selectedYear,
  selectedMonth,
  onDateChange,
}) => {
  const { t, i18n } = useTranslation('work');
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  // TODO: deprecated — replace with role-based check from JWT when Google OAuth is used in production
  const isAdmin = user?.username === 'derick' || user?.username === 'april';

  // Month keys for translation
  const monthKeys = [
    'january', 'february', 'march', 'april', 'may', 'june',
    'july', 'august', 'september', 'october', 'november', 'december'
  ];

  // Get selected user from URL query params (for admin)
  const selectedUser = searchParams.get('user') || user?.username || '';

  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [expandedShifts, setExpandedShifts] = useState(true); // Default to expanded
  const [editingNoteDate, setEditingNoteDate] = useState<string | null>(null);
  const [editingNoteValue, setEditingNoteValue] = useState('');

  const { data: salarySlip, isLoading, error, refetch } = useSalarySlip({
    year: selectedYear,
    month: selectedMonth,
    user: isAdmin ? selectedUser : undefined,
  });

  // Get employees list for admin dropdown
  const { data: employees } = useEmployees();

  // Hook for updating notes
  const updateNotesMutation = useUpdateWorkShiftNotes();

  // Handle user change for admin
  const handleUserChange = (newUser: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('user', newUser);
    setSearchParams(newParams);
    setIsConfirmed(false);
  };

  // Navigate to previous month
  const goToPreviousMonth = () => {
    setIsConfirmed(false);
    if (selectedMonth === 1) {
      onDateChange(selectedYear - 1, 12);
    } else {
      onDateChange(selectedYear, selectedMonth - 1);
    }
  };

  // Navigate to next month
  const goToNextMonth = () => {
    setIsConfirmed(false);
    if (selectedMonth === 12) {
      onDateChange(selectedYear + 1, 1);
    } else {
      onDateChange(selectedYear, selectedMonth + 1);
    }
  };

  const handleConfirm = async () => {
    setIsConfirming(true);
    // Simulate API call for confirmation
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsConfirming(false);
    setIsConfirmed(true);
  };

  const handleApprove = async () => {
    setIsApproving(true);
    // TODO: Implement actual approval API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsApproving(false);
    // TODO: Show success message
  };

  const formatCurrency = (amount: number): string => {
    return `NT$ ${amount.toLocaleString()}`;
  };

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    const locale = i18n.language === 'zh-TW' ? 'zh-TW' : 'en-US';
    return date.toLocaleDateString(locale, { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const getShiftTypeLabel = (shiftType: string): string => {
    const typeMap: Record<string, string> = {
      'morning': t('shift.types.morning'),
      'afternoon': t('shift.types.afternoon'),
      'night': t('shift.types.night'),
      'full-day': t('shift.types.fullDay'),
      'half-day': t('shift.types.halfDay'),
    };
    return typeMap[shiftType] || shiftType;
  };

  const getShiftTypeColor = (shiftType: string): string => {
    const colors: Record<string, string> = {
      'morning': 'bg-yellow-100 text-yellow-800',
      'afternoon': 'bg-orange-100 text-orange-800',
      'night': 'bg-indigo-100 text-indigo-800',
      'full-day': 'bg-green-100 text-green-800',
      'half-day': 'bg-blue-100 text-blue-800',
    };
    return colors[shiftType] || 'bg-gray-100 text-gray-800';
  };

  const getMonthName = (monthIndex: number): string => {
    return t(`salary.months.${monthKeys[monthIndex - 1]}`);
  };

  // Handle notes editing
  const startEditingNote = (shift: IWorkShift) => {
    setEditingNoteDate(shift.date);
    setEditingNoteValue(shift.notes || '');
  };

  const cancelEditingNote = () => {
    setEditingNoteDate(null);
    setEditingNoteValue('');
  };

  const saveNote = async (date: string) => {
    try {
      await updateNotesMutation.mutateAsync({
        user: selectedUser,
        date,
        notes: editingNoteValue,
      });
      setEditingNoteDate(null);
      setEditingNoteValue('');
      refetch();
    } catch (error) {
      logger.error('Failed to update note:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <svg className="animate-spin h-8 w-8 text-purple-600" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
        <span className="ml-3 text-gray-600 dark:text-gray-400">{t('salary.loading')}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-700">{t('salary.loadError')}</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      {/* Month/Year Selection */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('salary.title')}</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">{t('salary.subtitle')}</p>
        </div>
        <div className="flex items-center space-x-2">
          {/* Previous Month Button */}
          <button
            onClick={goToPreviousMonth}
            className="p-2 text-gray-600 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            title={t('salary.previousMonth')}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <select
            value={selectedMonth}
            onChange={(e) => {
              onDateChange(selectedYear, Number(e.target.value));
              setIsConfirmed(false);
            }}
            className="px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          >
            {monthKeys.map((key, index) => (
              <option key={key} value={index + 1}>{t(`salary.months.${key}`)}</option>
            ))}
          </select>
          <select
            value={selectedYear}
            onChange={(e) => {
              onDateChange(Number(e.target.value), selectedMonth);
              setIsConfirmed(false);
            }}
            className="px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          >
            {generateYearOptions().map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>

          {/* Next Month Button */}
          <button
            onClick={goToNextMonth}
            className="p-2 text-gray-600 dark:text-gray-300 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            title={t('salary.nextMonth')}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      {salarySlip && (
        <>
          {/* Salary Slip Card */}
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-md overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold">{t('salary.companyName')}</h3>
                  <p className="text-purple-200 text-sm mt-1">{t('salary.monthlyStatement')}</p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold">{getMonthName(selectedMonth)} {selectedYear}</div>
                  <div className="text-purple-200 text-sm">{t('salary.payPeriod')}</div>
                </div>
              </div>
            </div>

            {/* Employee Info */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
              <h4 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">{t('salary.employeeInfo.title')}</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-gray-600 dark:text-gray-400 text-sm">{t('salary.employeeInfo.name')}:</span>
                  {isAdmin && employees && employees.length > 0 ? (
                    <select
                      value={selectedUser}
                      onChange={(e) => handleUserChange(e.target.value)}
                      className="ml-2 px-2 py-1 border border-gray-300 rounded shadow-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white text-sm"
                    >
                      {employees.map((emp) => (
                        <option key={emp.username} value={emp.username}>
                          {emp.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className="ml-2 font-medium text-gray-900 dark:text-white">{salarySlip.employee.name}</span>
                  )}
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-400 text-sm">{t('salary.employeeInfo.username')}:</span>
                  <span className="ml-2 font-medium text-gray-900 dark:text-white">{salarySlip.employee.username}</span>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-400 text-sm">{t('salary.employeeInfo.bank')}:</span>
                  <span className="ml-2 font-medium text-gray-900 dark:text-white">{salarySlip.employee.bank} - {salarySlip.employee.bank_branch}</span>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-400 text-sm">{t('salary.employeeInfo.account')}:</span>
                  <span className="ml-2 font-medium text-gray-900 dark:text-white">{salarySlip.employee.account_last4}</span>
                </div>
              </div>
            </div>

            {/* Work Summary */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h4 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-4">{t('salary.workSummary.title')}</h4>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">{salarySlip.summary.total_days}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{t('salary.workSummary.workDays')}</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-blue-600">{salarySlip.summary.regular_hours.toFixed(1)}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{t('salary.workSummary.regularHours')}</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-amber-600">{salarySlip.summary.overtime_hours.toFixed(1)}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{t('salary.workSummary.overtimeHours')}</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-green-600">{salarySlip.summary.total_hours.toFixed(1)}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{t('salary.workSummary.totalHours')}</div>
                </div>
              </div>
            </div>

            {/* Expandable Work Hours Detail */}
            <div className="border-b border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setExpandedShifts(!expandedShifts)}
                className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="flex items-center">
                  <svg
                    className={`w-5 h-5 text-gray-500 dark:text-gray-400 transition-transform ${expandedShifts ? 'rotate-90' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  <span className="ml-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                    {t('salary.workDetail.title')} ({salarySlip.shifts?.length || 0} {t('salary.workDetail.records')})
                  </span>
                </div>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {expandedShifts ? t('salary.workDetail.clickToCollapse') : t('salary.workDetail.clickToExpand')}
                </span>
              </button>

              {expandedShifts && salarySlip.shifts && salarySlip.shifts.length > 0 && (
                <div className="px-4 pb-4">
                  {/* Mobile Card View */}
                  <div className="block sm:hidden space-y-3">
                    {salarySlip.shifts.map((shift: IWorkShift, index: number) => (
                      <div key={index} className="bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 p-4 shadow-sm">
                        {/* Header: Date and Hours */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <span className="text-base font-semibold text-gray-900 dark:text-white">
                              {formatDate(shift.date)}
                            </span>
                            <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${getShiftTypeColor(shift.shift_type)}`}>
                              {getShiftTypeLabel(shift.shift_type)}
                            </span>
                          </div>
                          <div className="text-right">
                            <span className={`text-lg font-bold ${shift.hours_worked > 8 ? 'text-amber-600' : 'text-green-600'}`}>
                              {shift.hours_worked.toFixed(1)}h
                            </span>
                            {shift.hours_worked > 8 && (
                              <span className="ml-1 text-xs text-amber-500">{t('salary.workDetail.overtime')}</span>
                            )}
                          </div>
                        </div>

                        {/* Time and Break */}
                        <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-300 mb-2">
                          <div className="flex items-center gap-1">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>{shift.start_time} - {shift.end_time}</span>
                          </div>
                          <div className="flex items-center gap-1 text-gray-500">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                            </svg>
                            <span>{shift.break_duration} {t('salary.workDetail.min')}</span>
                          </div>
                        </div>

                        {/* Notes */}
                        <div className="pt-2 border-t border-gray-100 dark:border-gray-600">
                          <div className="flex items-start gap-1">
                            <svg className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            {editingNoteDate === shift.date ? (
                              <div className="flex-1 flex items-center gap-2">
                                <input
                                  type="text"
                                  value={editingNoteValue}
                                  onChange={(e) => setEditingNoteValue(e.target.value)}
                                  className="flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-gray-500 rounded dark:bg-gray-600 dark:text-white"
                                  autoFocus
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') saveNote(shift.date);
                                    if (e.key === 'Escape') cancelEditingNote();
                                  }}
                                />
                                <button
                                  onClick={() => saveNote(shift.date)}
                                  disabled={updateNotesMutation.isLoading}
                                  className="p-1.5 text-green-600 hover:text-green-700 bg-green-50 dark:bg-green-900/20 rounded"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                </button>
                                <button
                                  onClick={cancelEditingNote}
                                  className="p-1.5 text-gray-400 hover:text-gray-600 bg-gray-100 dark:bg-gray-600 rounded"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              </div>
                            ) : (
                              <div
                                onClick={() => isAdmin && startEditingNote(shift)}
                                className={`flex-1 text-sm text-gray-600 dark:text-gray-300 ${isAdmin ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600 rounded px-1 -mx-1 py-0.5' : ''}`}
                              >
                                {shift.notes || <span className="text-gray-400 italic">{isAdmin ? t('salary.workDetail.clickToEdit') : '-'}</span>}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    {/* Mobile Total */}
                    <div className="bg-gray-100 dark:bg-gray-600 rounded-lg p-4 flex justify-between items-center">
                      <span className="font-semibold text-gray-700 dark:text-gray-200">{t('salary.workDetail.total')}</span>
                      <span className="text-lg font-bold text-green-600">{salarySlip.summary.total_hours.toFixed(1)}h</span>
                    </div>
                  </div>

                  {/* Desktop Table View */}
                  <div className="hidden sm:block bg-gray-50 dark:bg-gray-700 rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                      <thead className="bg-gray-100 dark:bg-gray-600">
                        <tr>
                          <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('salary.workDetail.date')}</th>
                          <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('salary.workDetail.type')}</th>
                          <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('salary.workDetail.time')}</th>
                          <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('salary.workDetail.break')}</th>
                          <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('salary.workDetail.hours')}</th>
                          <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('salary.workDetail.notes')}</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-700 divide-y divide-gray-200 dark:divide-gray-600">
                        {salarySlip.shifts.map((shift: IWorkShift, index: number) => (
                          <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-600">
                            <td className="px-3 py-3 whitespace-nowrap">
                              <span className="text-sm font-medium text-gray-900 dark:text-white">
                                {formatDate(shift.date)}
                              </span>
                            </td>
                            <td className="px-3 py-3 whitespace-nowrap">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getShiftTypeColor(shift.shift_type)}`}>
                                {getShiftTypeLabel(shift.shift_type)}
                              </span>
                            </td>
                            <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                              {shift.start_time} - {shift.end_time}
                            </td>
                            <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                              {shift.break_duration} {t('salary.workDetail.min')}
                            </td>
                            <td className="px-3 py-3 whitespace-nowrap text-sm font-medium text-right">
                              <span className={shift.hours_worked > 8 ? 'text-amber-600' : 'text-gray-900 dark:text-white'}>
                                {shift.hours_worked.toFixed(1)}h
                              </span>
                              {shift.hours_worked > 8 && (
                                <span className="ml-1 text-xs text-amber-500">{t('salary.workDetail.overtime')}</span>
                              )}
                            </td>
                            <td className="px-3 py-3 text-sm text-gray-600 dark:text-gray-300">
                              {editingNoteDate === shift.date ? (
                                <div className="flex items-center space-x-1">
                                  <input
                                    type="text"
                                    value={editingNoteValue}
                                    onChange={(e) => setEditingNoteValue(e.target.value)}
                                    className="w-24 px-2 py-1 text-xs border border-gray-300 dark:border-gray-500 rounded dark:bg-gray-600 dark:text-white"
                                    autoFocus
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') saveNote(shift.date);
                                      if (e.key === 'Escape') cancelEditingNote();
                                    }}
                                  />
                                  <button
                                    onClick={() => saveNote(shift.date)}
                                    disabled={updateNotesMutation.isLoading}
                                    className="p-1 text-green-600 hover:text-green-700"
                                    title="Save"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                  </button>
                                  <button
                                    onClick={cancelEditingNote}
                                    className="p-1 text-gray-400 hover:text-gray-600"
                                    title="Cancel"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                </div>
                              ) : (
                                <div
                                  onClick={() => isAdmin && startEditingNote(shift)}
                                  className={`min-w-[60px] ${isAdmin ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-500 rounded px-1 -mx-1' : ''}`}
                                  title={isAdmin ? t('salary.workDetail.clickToEdit') : undefined}
                                >
                                  {shift.notes || (isAdmin ? <span className="text-gray-400 italic text-xs">-</span> : '-')}
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-gray-100 dark:bg-gray-600">
                        <tr>
                          <td colSpan={5} className="px-3 py-3 text-sm font-semibold text-gray-700 dark:text-gray-200">
                            {t('salary.workDetail.total')}
                          </td>
                          <td className="px-3 py-3 text-sm font-bold text-right text-green-600">
                            {salarySlip.summary.total_hours.toFixed(1)}h
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}

              {expandedShifts && (!salarySlip.shifts || salarySlip.shifts.length === 0) && (
                <div className="px-4 pb-4">
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <svg className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    <p>{t('salary.workDetail.noRecords')}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Earnings */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h4 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-4">{t('salary.earnings.title')}</h4>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-700 dark:text-gray-300">{t('salary.earnings.baseSalary')}</span>
                  <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(salarySlip.earnings.base_salary)}</span>
                </div>
                {salarySlip.earnings.hourly_pay > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700 dark:text-gray-300">
                      {t('salary.earnings.hourlyPay')} ({salarySlip.summary.regular_hours.toFixed(1)}h × {formatCurrency(salarySlip.employee.hourly_salary)}{t('salary.earnings.perHour')})
                    </span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {formatCurrency(salarySlip.earnings.hourly_pay)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-gray-700 dark:text-gray-300">
                    {t('salary.earnings.overtimePay')} ({salarySlip.summary.overtime_hours.toFixed(1)}h × {formatCurrency(salarySlip.employee.overtime_pay || salarySlip.employee.hourly_salary)}{t('salary.earnings.perHour')})
                  </span>
                  <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(salarySlip.earnings.overtime_pay)}</span>
                </div>
                <div className="flex justify-between items-center pt-3 border-t border-gray-100 dark:border-gray-600">
                  <span className="font-semibold text-gray-900 dark:text-white">{t('salary.earnings.grossSalary')}</span>
                  <span className="font-bold text-green-600 text-lg">{formatCurrency(salarySlip.earnings.gross_salary)}</span>
                </div>
              </div>
            </div>

            {/* Deductions */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
              <h4 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-4">{t('salary.deductions.title')}</h4>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-700 dark:text-gray-300">{t('salary.deductions.laborInsurance')}</span>
                  <span className="font-medium text-red-600">- {formatCurrency(salarySlip.deductions.labor_insurance)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-700 dark:text-gray-300">{t('salary.deductions.healthcarePension')}</span>
                  <span className="font-medium text-red-600">- {formatCurrency(salarySlip.deductions.healthcare_pension)}</span>
                </div>
                <div className="flex justify-between items-center pt-3 border-t border-gray-200 dark:border-gray-600">
                  <span className="font-semibold text-gray-900 dark:text-white">{t('salary.deductions.totalDeductions')}</span>
                  <span className="font-bold text-red-600">- {formatCurrency(salarySlip.deductions.total)}</span>
                </div>
              </div>
            </div>

            {/* Net Salary */}
            <div className="p-6 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20">
              <div className="flex justify-between items-center">
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white">{t('salary.netSalary.title')}</h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('salary.netSalary.subtitle')}</p>
                </div>
                <div className="text-3xl font-bold text-green-600">
                  {formatCurrency(salarySlip.net_salary)}
                </div>
              </div>
            </div>

            {/* Confirmation Section */}
            <div className="p-6 border-t border-gray-200 dark:border-gray-700">
              {isConfirmed ? (
                <div className="flex items-center justify-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <svg className="w-6 h-6 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-green-700 dark:text-green-400 font-medium">
                    {t('salary.confirmation.confirmed', { date: new Date().toLocaleDateString(i18n.language === 'zh-TW' ? 'zh-TW' : 'en-US') })}
                  </span>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t('salary.confirmation.disclaimer')}
                  </p>
                  <div className="flex space-x-3">
                    {/* Confirm Button - for the employee viewing their own slip */}
                    <button
                      onClick={handleConfirm}
                      disabled={isConfirming}
                      className="px-6 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isConfirming ? (
                        <span className="flex items-center">
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          {t('salary.confirmation.confirming')}
                        </span>
                      ) : (
                        t('salary.confirmation.confirmButton')
                      )}
                    </button>

                    {/* Approve Button - only for admin (derick) */}
                    {isAdmin && (
                      <button
                        onClick={handleApprove}
                        disabled={isApproving}
                        className="px-6 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isApproving ? (
                          <span className="flex items-center">
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            {t('salary.confirmation.approving')}
                          </span>
                        ) : (
                          t('salary.confirmation.approveButton')
                        )}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Print Button */}
          <div className="mt-6 flex justify-end">
            <button
              onClick={() => window.print()}
              className="flex items-center px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              {t('salary.print')}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default SalarySlip;
